import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { db as firestoreDb, fn } from 'firebase-modules';

import type { RootStore } from '.';

interface SessionData {
  name?: string;
  createdBy?: {
    email?: string;
  };
  updatedAt?: {
    toDate(): Date;
  };
  createdAt?: {
    toDate(): Date;
  };
  cardIds?: string[];
  sourceIds?: string[];
}

interface OrganizationStats {
  totalProjects: number;
  totalStorageBytes: number;
  memberCount: number;
  projects: Array<{
    id: string;
    name: string;
    ownerEmail: string;
    lastModified: Date;
    storageBytes: number;
  }>;
}

export class UiOrganizationStatsStore {
  stats: OrganizationStats = {
    totalProjects: 0,
    totalStorageBytes: 0,
    memberCount: 0,
    projects: [],
  };
  isLoading = false;
  error: string | null = null;

  constructor(_rootStore: RootStore) {
    makeObservable(this, {
      stats: observable,
      isLoading: observable,
      error: observable,
      loadOrganizationStats: action.bound,
      totalStorageGB: computed,
    });
  }

  async loadOrganizationStats(organizationId: string) {
    this.isLoading = true;
    this.error = null;

    try {
      // First get all organization members
      const membersSnapshot = await firestoreDb
        .collection('organizations')
        .doc(organizationId)
        .collection('members')
        .get();

      const memberIds = membersSnapshot.docs.map((doc) => doc.id);

      if (memberIds.length === 0) {
        runInAction(() => {
          this.stats = {
            totalProjects: 0,
            totalStorageBytes: 0,
            memberCount: 0,
            projects: [],
          };
          this.isLoading = false;
        });
        return;
      }

      // Get all sessions for organization members via cloud function (Admin SDK bypasses
      // Firestore list-query restrictions for org admins reading member-owned sessions).
      const allSessions: Array<{ id: string; data: SessionData }> = [];

      try {
        const listMemberSessionsFn = fn.httpsCallable('listOrganizationMemberSessions');
        const result = await listMemberSessionsFn({ organizationId });
        const memberSessions = (
          result.data as {
            sessions?: Array<{
              id: string;
              name?: string;
              createdBy?: { email?: string | null };
              createdAt?: string;
              cardCount?: number;
            }>;
          }
        ).sessions;

        memberSessions?.forEach((session) => {
          allSessions.push({
            id: session.id,
            data: {
              name: session.name,
              createdBy: session.createdBy
                ? { email: session.createdBy.email ?? undefined }
                : undefined,
              createdAt: session.createdAt
                ? { toDate: () => new Date(session.createdAt as string) }
                : undefined,
              cardIds: Array.from({ length: session.cardCount || 0 }),
              sourceIds: [],
            },
          });
        });
      } catch (cloudError) {
        // eslint-disable-next-line no-console
        console.warn(
          'Failed to load organization member sessions via cloud function, falling back to client queries:',
          cloudError,
        );

        const sessionPromises = memberIds.map(async (memberId) => {
          try {
            const memberSessions = await firestoreDb
              .collection('sessions')
              .where('userId', '==', memberId)
              .get();

            return memberSessions.docs.map((doc) => ({ id: doc.id, data: doc.data() }));
          } catch (memberError) {
            // eslint-disable-next-line no-console
            console.warn(`Failed to load sessions for member ${memberId}:`, memberError);
            return [];
          }
        });

        const sessionResults = await Promise.all(sessionPromises);
        sessionResults.flat().forEach((session) => {
          allSessions.push(session);
        });
      }

      const projects: OrganizationStats['projects'] = [];
      let totalStorageBytes = 0;

      // Process each session
      for (const session of allSessions) {
        // Calculate storage size (this is a simplified calculation)
        // In a real implementation, you'd calculate based on actual file sizes
        const estimatedStorageBytes = UiOrganizationStatsStore.estimateSessionStorage(session.data);
        totalStorageBytes += estimatedStorageBytes;

        projects.push({
          id: session.id,
          name: session.data.name || 'Untitled Session',
          ownerEmail: session.data.createdBy?.email || 'Unknown',
          lastModified:
            session.data.updatedAt?.toDate() || session.data.createdAt?.toDate() || new Date(),
          storageBytes: estimatedStorageBytes,
        });
      }

      runInAction(() => {
        this.stats = {
          totalProjects: projects.length,
          totalStorageBytes,
          memberCount: membersSnapshot.size,
          projects: projects.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime()),
        };
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load organization stats';
        this.isLoading = false;
      });
    }
  }

  private static estimateSessionStorage(sessionData: SessionData): number {
    // Simplified storage estimation
    // In reality, you'd calculate based on:
    // - Source file sizes
    // - Generated cards
    // - Images and assets
    // - Comments and metadata

    let estimatedBytes = 0;

    // Base session metadata
    estimatedBytes += JSON.stringify(sessionData).length;

    // Estimate based on number of cards
    const cardCount = sessionData.cardIds?.length || 0;
    estimatedBytes += cardCount * 1000; // ~1KB per card

    // Estimate based on number of sources
    const sourceCount = sessionData.sourceIds?.length || 0;
    estimatedBytes += sourceCount * 50000; // ~50KB per source

    return estimatedBytes;
  }

  get totalStorageGB(): number {
    return this.stats.totalStorageBytes / (1024 * 1024 * 1024);
  }
}
